// Form Component to Add or Edit project task

import { useEffect, useState } from "react";
import {
  Button,
  Form,
  Input,
  Row,
  Col,
  Select,
  Space,
  DatePicker,
  Upload,
  Checkbox,
  TimePicker,
} from "antd";
import FormLayout, {
  FormContent,
  FormFooter,
  FormHeader,
} from "../../../../../components/form/form";
import { FormRow } from "../../../../../components/form/form-row";
import {
  TProjectTask,
  TDropdownOptions,
  TResponseModel,
} from "../../../../../types/config";
import {
  eDocType,
  eMONTHMAPPING,
  ePriority,
  eResultCode,
  eRoleUniqueId,
  eSCHEDULETYPE,
  eTaskStatus,
  eWEEKDAYS,
} from "../../../../../utils/enum";
import useFetch from "../../../../../hooks/useFetch";
import useToast from "../../../../../hooks/useToast";
import { ToastOpen, ToastType } from "../../../../../state/toast/slice";
import {
  AddEditConfigGroup,
  AddEditTask,
  GetSpecificConfigGroup,
  GetSpecificProject,
  GetSpecificProjectDashboardData,
  GetSpecificTask,
  DeleteDocs,
  GetUserList,
} from "../../../../../utils/api.constant";
import TextArea from "antd/es/input/TextArea";
import { requiredFieldValidator } from "../../../../../utils/input.validator";
import {
  CheckOutlined,
  CloseOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import CustomDropdown from "../../../../../components/custom-dropdown/custom-dropdown";
import { PageSize } from "../../../../../utils/constants";
import dayjs from "dayjs";
import JoditEditor from "jodit-react";
import { useRef } from "react";

type TDrawerProps = {
  projectId: string;
  projectWorkMappingId?: number;
  statusId?: number;
  id: number;
  onCloseDrawer: () => void;
  onRefreshList: (data: any) => void;
  projectName?: string;
};

/**
 * Form Component to Add or Edit Config Group
 *
 * @param {TDrawerProps} props - drawer props
 * @returns {JSX.Element}
 */
export default function AddEditTaskForm(props: TDrawerProps) {
  const [form] = Form.useForm(); // Form instance
  const [selectedStatus, setSelectedStatus] = useState<string[]>([]);
  const [statusOptions, setStatusOptions] = useState<TDropdownOptions[]>([]);
  const [workCategoryOptions, setWorkCategoryOptions] = useState<
    TDropdownOptions[]
  >([]);
  const [isLoading, setIsLoading] = useState<boolean>(false); // Loading state
  const { post } = useFetch(); // Hook to make API calls
  const { onShowToast } = useToast(); // Hook to show toast
  // const [isRecurring, setIsRecurring] = useState(false);
  const [scheduleType, setScheduleType] = useState(null);
  const [description, setDescription] = useState("");
  const editor = useRef(null);
  const [userOptions, setUserOptions] = useState<TDropdownOptions[]>([]);
  const [monthDays, setMonthDays] = useState<TDropdownOptions[]>([
    { label: "Start of Month", value: eMONTHMAPPING.STARTOFMONTH },
    { label: "Mid of Month", value: eMONTHMAPPING.MIDOFMONTH },
    { label: "End of Month", value: eMONTHMAPPING.ENDOFMONTH },
  ]);
  const [initialDocuments, setInitialDocuments] = useState<string[]>([]);
  const [documentsToDelete, setDocumentsToDelete] = useState<string[]>([]);

  // default values initial state
  // Hook to handle on load operations

  const defaultValues: TProjectTask = {
    id: 0,
    name: "",
    description: "",
    projectId: props.projectId,
    statusId: Number(props.statusId),
    projectWorkMappingId: props.projectWorkMappingId || null,
    assignToUserId: [] as number[],
    startDateTime: "",
    endDateTime: "",
    // isRecurring: false,
    estimatedTime: "",
    priority: null,
  };

  const priorityOptions = [
    {
      label: "Low",
      value: ePriority.LOW,
    },
    {
      label: "Medium",
      value: ePriority.MEDIUM,
    },
    {
      label: "High",
      value: ePriority.HIGH,
    },
  ];

  const taskStatusOptions = [
    {
      label: "Internal",
      value: eTaskStatus.INTERNAL,
    },
    {
      label: "External",
      value: eTaskStatus.EXTERNAL,
    },
  ];

  const editorConfig = {
    readonly: false,
    height: 150,
    toolbarAdaptive: false,
    toolbarSticky: true,
    buttons: "bold,italic,underline,ul,ol,font,fontsize,align,link",
    style: {
      fontFamily: "inherit",
      fontSize: "14px",
    },
  };

  const deleteDocuments = async (docUrls: string[]) => {
    if (docUrls.length === 0) return;

    try {
      const response = await post(DeleteDocs, {
        data: {
          docUrls: docUrls,
        },
      });

      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        // Documents deleted successfully
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error deleting documents:", error);
    }
  };

  const uploadDocuments = async (
    attachments: any,
    taskId: number,
    projectId: number
  ) => {
    try {
      // Filter only new files (ones with originFileObj)
      const newFiles = attachments.filter((file: any) => file.originFileObj);

      if (newFiles.length === 0) {
        return;
      }

      const formData = new FormData();

      newFiles.forEach((file: any) => {
        formData.append("files", file.originFileObj);
      });

      formData.append("taskId", taskId.toString());
      formData.append("projectId", projectId.toString());
      formData.append("docType", eDocType.TASK_MEDIA.toString());

      const uploadResponse = await post("/uploadMedia", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (uploadResponse.dataResponse.returnCode === eResultCode.SUCCESS) {
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: uploadResponse.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  // Handle on submit event
  const onFinish = async (values: TProjectTask) => {
    try {
      const { attachments, ...restValues } = values;
      const payload = {
        data: {
          ...restValues,
          description: description, // Use the rich text content
          projectId: props.projectId,
          id: props.id ? props.id : 0,
          projectWorkMappingId:
            values.projectWorkMappingId || props.projectWorkMappingId,
          startDateTime: dayjs(values.startDateTime),
          endDateTime: dayjs(values.endDateTime),
          // isRecurring: values.isRecurring ? 1 : 0,
        },
      };
      setIsLoading(true);

      const response = await post(AddEditTask, payload);
      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        // Delete documents marked for deletion
        if (documentsToDelete.length > 0) {
          await deleteDocuments(documentsToDelete);
        }

        if (values.attachments?.length) {
          uploadDocuments(
            values.attachments,
            response.data?.id,
            Number(props.projectId)
          );
        }
        form.resetFields();
        // Get employee names for display
        const assignedEmployeeNames = Array.isArray(values.assignToUserId)
          ? values.assignToUserId
              .map((userId) => {
                const user = userOptions.find(
                  (option) => option.value === userId
                );
                return user ? user.label : "";
              })
              .filter((name) => name)
              .join(", ")
          : "";

        props.onRefreshList({
          taskCategoryId: response.data?.id || response?.data?.newTask?.id,
          taskCategoryName: values.name,
          taskDescription: values.description,
          employeeName: assignedEmployeeNames,
          assignToUserId: values.assignToUserId,
          priority: values.priority,
          startDate: values.startDateTime, // Format: DD-MMM-YYYY
          endDate: values.endDateTime, // Format: DD-MMM-YYYY
          timeLeft: "", // Format: X Hours
          estimatedTime: values.estimatedTime,
          projectWorkMappingId:
            values.projectWorkMappingId || props.projectWorkMappingId,
          statusId: values.statusId,
        });
        props.onCloseDrawer();
      } else {
        // onShowToast({
        //   type: ToastType.error,
        //   title: <CloseOutlined />,
        //   position: ToastOpen.centerTop,
        //   content: response.dataResponse.description,
        // });
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getProjectData = async (projectId: string) => {
    try {
      // Get project details for status options
      const projectResponse: TResponseModel = await post(GetSpecificProject, {
        data: { id: projectId },
      });

      if (projectResponse.dataResponse.returnCode === eResultCode.SUCCESS) {
        const statusOptions =
          projectResponse.data?.projectcategory?.categoryStatus?.map(
            (item: any) => ({
              label: item.name,
              value: item.id,
            })
          );
        setStatusOptions(statusOptions);
      }

      // Get work categories from dashboard data
      const dashboardResponse: TResponseModel = await post(
        GetSpecificProjectDashboardData,
        {
          data: { id: projectId },
        }
      );

      if (dashboardResponse.dataResponse.returnCode === eResultCode.SUCCESS) {
       form.setFieldValue("project", dashboardResponse.data?.project?.projectName);

        // dashboardResponse.data.projectStatusList.forEach((category: any) => {
        //   category.workList.forEach((work: any) => {
        //     const key = work.projectWorkMappingId;
        //     if (!workCategoriesMap.has(key)) {
        //       workCategoriesMap.set(key, {
        //         label: `${work.workCategoryName} - ${work.workTitle}`,
        //         value: work.projectWorkMappingId,
        //       });
        //     }
        //   });
        // });
        // console.log(workCategoriesMap)

        const uniqueWorks= dashboardResponse.data.workList.map((category: any) => {
          return {
            label: category.workTitle + " - " + category.workCategoryName,
            value: category.projectWorkMappingId,
          };
        });
        setWorkCategoryOptions(uniqueWorks);
      }
    } catch (error) {
      console.error("Error fetching project data:", error);
      onShowToast({
        type: "error",
        title: <CloseOutlined />,
        content: "Failed to fetch project data",
      });
    }
  };

  const getUserList = async () => {
    try {
      const requestPayload = {
        data: {
          // roleUniqueId: eRoleUniqueId.EMPLOYEE,
          projectId: props.projectId,
          pageSize: 100,
          currentPage: 1,
        },
      };

      const response: TResponseModel = await post(
       GetUserList,
        requestPayload
      );
      const { data, dataResponse } = response;
      if (dataResponse.returnCode === eResultCode.SUCCESS) {
        const Options = data?.map((item: any) => ({
          label: item.fullName,
          value: item.id,
        }));

        setUserOptions(Options);
      } else {
        onShowToast({
          type: "error",
          title: <CloseOutlined />,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const fetchSpecificTask = async (id: number) => {
    try {
      const requestPayload = {
        data: {
          id: id,
        },
      };

      const response: TResponseModel = await post(
        GetSpecificTask,
        requestPayload
      );
      const { data, dataResponse } = response;
      if (dataResponse.returnCode === eResultCode.SUCCESS) {
        // Store initial document URLs for deletion tracking
        const docUrls =
          data.task.taskdocuments?.map((doc: any) => doc.docURL) || [];
        setInitialDocuments(docUrls);

        // Convert taskdocuments to Upload component format
        const existingDocuments =
          data.task.taskdocuments?.map((doc: any, index: number) => ({
            uid: `${index}-${doc.docName}`,
            name: doc.docName,
            status: "done",
            url: `${process.env.REACT_APP_PUBLIC_API_IMAGEURL}/${doc.docURL}`,
            thumbUrl: `${process.env.REACT_APP_PUBLIC_API_IMAGEURL}/${doc.docURL}`,
          })) || [];

        form.setFieldsValue({
          name: data.task.name,
          assignToUserId: Array.isArray(data.task.assignToUserId)
            ? data.task.assignToUserId
            : data.task.assignToUserId
              ? [data.task.assignToUserId]
              : [],
          startDateTime: data.task.startDateTime
            ? dayjs(data.task.startDateTime?.slice(0, 10))
            : "",
          endDateTime: data.task.endDateTime
            ? dayjs(data.task.endDateTime?.slice(0, 10))
            : "",
          taskType: data.task.taskType,
          priority: data.task.priority,
          statusId: data.task.statusId,
          estimatedTime: data.task.estimatedTime,
          description: data.task.description,
          // isRecurring: data.task.isRecurring,
          attachments: existingDocuments,
        });
        setDescription(data.task.description || "");
        // setIsRecurring(data?.isRecurring === 1 ? true : false);
      } else {
        onShowToast({
          type: "error",
          title: <CloseOutlined />,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error fetching customers:", error);
    }
  };

  useEffect(() => {
    if (props.projectId) {
     
        getProjectData(props.projectId);
      
      getUserList();
    }
  }, [props.projectId]);

  useEffect(() => {
    if (props.id) {
      fetchSpecificTask(props.id);
    }
  }, []);

  return (
    <FormLayout>
      <Form
        form={form}
        name='register'
        onFinish={onFinish}
        initialValues={defaultValues}
        layout='vertical'
        // onValuesChange={(changedValues) => {
        //   if (changedValues.isRecurring !== undefined) {
        //     setIsRecurring(changedValues.isRecurring);
        //   }
        //   if (changedValues.isRecurring) {
        //     form.setFieldValue("endDateTime", "");
        //   }
        // }}
      >
        <FormHeader>
          <CloseOutlined
            style={{ cursor: "pointer", marginTop: "0.3rem", color: "white" }}
            onClick={() => {
              props.onCloseDrawer();
            }}
          />
        </FormHeader>
        <FormContent>
          {/* <Row gutter={16}>
            <Space>
              <Col span={24}>
                <h2 style={{ color: "#fff", marginTop: "10px" }}>
                  {`${props.id ? "Edit" : "Add"} a Project Task`}
                </h2>
              </Col>
            </Space>
          </Row> */}
          <Row
            gutter={24}
            className='formRowHeight'
          >
            <Col span={24}>
              <Form.Item
                name='name'
                label='Task Name'
                rules={[
                  {
                    required: true,
                    validator: requiredFieldValidator("select task name"),
                  },
                ]}
              >
                <Input placeholder='Enter Task name' />
              </Form.Item>
            </Col>
          </Row>
          <Row
            gutter={24}
            className='formRowHeight'
          >
            <Col span={12}>
              <Form.Item label='Project'>
                <Input
                  name="project"
                  value={props.projectName || form.getFieldValue("project")}
                  disabled
                  placeholder='Project Name'
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name='projectWorkMappingId'
                label='Work'
                rules={[
                  {
                    required: !props.projectWorkMappingId,
                    validator: requiredFieldValidator("select work"),
                  },
                ]}
              >
                <Select
                  placeholder='Select Work'
                  style={{ width: "100%" }}
                  disabled={!!props.projectWorkMappingId}
                  showSearch
                  filterOption={(input, option) =>
                    String(option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                >
                  {workCategoryOptions.map((category: TDropdownOptions) => (
                    <Select.Option
                      key={category.value}
                      value={category.value}
                    >
                      {category.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row
            gutter={24}
            className='formRowHeight'
          >
            <Col span={24}>
              <Form.Item
                name='assignToUserId'
                label='Assigned To'
                rules={[
                  {
                    required: true,
                    validator: requiredFieldValidator("select assigned to"),
                  },
                ]}
              >
                <Select
                  mode='multiple'
                  placeholder='Select Employee Names'
                  style={{ width: "100%" }}
                  options={userOptions}
                  showSearch
                  filterOption={(input, option) =>
                    String(option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  maxTagCount='responsive'
                />
              </Form.Item>
            </Col>
          </Row>
          <Row
            gutter={24}
            className='formRowHeight'
          >
            <Col span={12}>
              <Form.Item
                name='priority'
                label='Priority'
                rules={[
                  {
                    required: true,
                    validator: requiredFieldValidator("select priority"),
                  },
                ]}
              >
                <Select
                  placeholder='Select Priority'
                  style={{ width: "100%" }}
                >
                  {priorityOptions.map((priority: TDropdownOptions) => (
                    <Select.Option
                      key={priority.value}
                      value={priority.value}
                    >
                      {priority.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name='statusId'
                label='Status'
                rules={[
                  {
                    required: true,
                    validator: requiredFieldValidator("select status"),
                  },
                ]}
              >
                <Select
                  placeholder='Select Status'
                  style={{ width: "100%" }}
                  disabled={true}
                >
                  {statusOptions?.map((status: TDropdownOptions) => (
                    <Select.Option
                      key={status.value}
                      value={status.value}
                    >
                      {status.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row
            gutter={24}
            className='formRowHeight'
          >
            <Col span={12}>
              <Form.Item
                name='estimatedTime'
                label='Estimated Time (In Minutes)'
                rules={[
                  {
                    required: true,
                    validator: requiredFieldValidator("select estimatedTime"),
                  },
                  {
                    pattern: /^[1-9]\d*$/,
                    message: "Please enter a positive number",
                  },
                ]}
              >
                <Input placeholder='Enter Estimated Time' />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name='taskType'
                label='Task Type'
                rules={[
                  {
                    required: true,
                    validator: requiredFieldValidator("select task type"),
                  },
                ]}
              >
                <Select
                  placeholder='Select Task Type'
                  style={{ width: "100%" }}
                >
                  {taskStatusOptions.map((status: TDropdownOptions) => (
                    <Select.Option
                      key={status.value}
                      value={status.value}
                    >
                      {status.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row
            gutter={24}
            className='formRowHeight'
          >
            <Col span={12}>
              <Form.Item
                name='startDateTime'
                label='Start Date'
                required
              >
                <DatePicker
                  style={{ width: "100%" }}
                  placeholder='Select Start Date'
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name='endDateTime'
                label='End Date'
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value) {
                        return Promise.reject(
                          new Error("End Date is required")
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  placeholder='Select End Date'
                  // disabled={isRecurring}
                />
              </Form.Item>
              {/* <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <Form.Item
                  name='isRecurring'
                  valuePropName='checked'
                  style={{ position: "relative", bottom: "28px" }}
                >
                  <Checkbox
                    onChange={(e) => {
                      form.setFieldsValue({ isRecurring: e.target.checked });
                    }}
                  >
                    Recurring Task
                  </Checkbox>
                </Form.Item>
              </div> */}
            </Col>
          </Row>
          {/* {isRecurring && (
            <>
              <Row
                gutter={16}
                className='formRowHeight'
              >
                <Col span={24}>
                  <Form.Item
                    name='workScheduleType'
                    valuePropName='workScheduleType'
                    label='Schedule Type'
                    rules={[
                      {
                        required: true,
                        message: "Please select schedule type",
                      },
                    ]}
                  >
                    <Select
                      placeholder='Select schedule type'
                      options={[
                        { label: "Daily", value: eSCHEDULETYPE.DAILY },
                        { label: "Weekly", value: eSCHEDULETYPE.WEEKLY },
                        { label: "Monthly", value: eSCHEDULETYPE.MONTHLY },
                      ]}
                      onChange={(value: any) => setScheduleType(value)}
                    />
                  </Form.Item>
                </Col>
              </Row>
              {(scheduleType === eSCHEDULETYPE.WEEKLY ||
                scheduleType === eSCHEDULETYPE.MONTHLY) && (
                <>
                  <Row
                    gutter={16}
                    className='formRowHeight'
                  >
                    <Col span={24}>
                      <Form.Item
                        name='day'
                        label='Day'
                        rules={[
                          { required: true, message: "Please select day" },
                        ]}
                      >
                        <Select
                          mode='multiple'
                          placeholder='Select day(s)'
                          options={
                            scheduleType === eSCHEDULETYPE.WEEKLY
                              ? [
                                  {
                                    label: "Sunday",
                                    value: eWEEKDAYS.SUNDAY,
                                  },
                                  {
                                    label: "Monday",
                                    value: eWEEKDAYS.MONDAY,
                                  },
                                  {
                                    label: "Tuesday",
                                    value: eWEEKDAYS.TUESDAY,
                                  },
                                  {
                                    label: "Wednesday",
                                    value: eWEEKDAYS.WEDNESDAY,
                                  },
                                  {
                                    label: "Thursday",
                                    value: eWEEKDAYS.THURSDAY,
                                  },
                                  {
                                    label: "Friday",
                                    value: eWEEKDAYS.FRIDAY,
                                  },
                                  {
                                    label: "Saturday",
                                    value: eWEEKDAYS.SATURDAY,
                                  },
                                ]
                              : monthDays
                          }
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </>
              )}
              <Row
                gutter={16}
                className='formRowHeight'
              >
                <Col span={24}>
                  <Form.Item
                    name='scheduleTime'
                    label='Start Time'
                    rules={[{ required: true, message: "Please select time" }]}
                  >
                    <TimePicker
                      format='HH:mm:ss'
                      placeholder='Select start time'
                      use12Hours={false}
                      style={{ width: "100%" }}
                      onChange={(time, timeString) => {
                        form.setFieldValue("scheduleTime", time);
                      }}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </>
          )} */}
          <Row
            gutter={16}
            className='formRowHeight'
            style={{
              marginBottom: "130px",
            }}
          >
            <Col span={24}>
              <Form.Item
                name='description'
                label='Description'
                rules={[
                  {
                    required: true,
                    validator: (_, value) => {
                      if (
                        !description ||
                        description.trim() === "" ||
                        description === "<p><br></p>"
                      ) {
                        return Promise.reject(
                          new Error("Please enter description")
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <div
                  style={{ border: "1px solid #d9d9d9", borderRadius: "6px" }}
                >
                  <JoditEditor
                    ref={editor}
                    value={description}
                    config={editorConfig}
                    onBlur={(newContent) => {
                      setDescription(newContent);
                      form.setFieldValue("description", newContent);
                    }}
                    onChange={(newContent) => {}}
                  />
                </div>
              </Form.Item>
            </Col>
          </Row>
          <Row
            gutter={16}
            className='formRowHeight !mt-10'
          >
            <Col span={24}>
              <Form.Item
                name='attachments'
                label='Attachments'
                valuePropName='fileList'
                getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
              >
                <Upload
                  name='files'
                  action='/upload'
                  listType='picture-card'
                  multiple={true}
                  beforeUpload={() => false}
                  onChange={({ fileList }) => {
                    form.setFieldValue("attachments", fileList);
                  }}
                  onRemove={(file) => {
                    if (file.url) {
                      // Extract the doc_url part from the full URL
                      const urlParts = file.url.split("//");
                      const docUrl =
                        urlParts.length > 1
                          ? urlParts[urlParts.length - 1]
                          : file.url.replace(
                              `${process.env.REACT_APP_PUBLIC_API_IMAGEURL}/`,
                              ""
                            );

                      if (initialDocuments.includes(docUrl)) {
                        setDocumentsToDelete((prev) => [...prev, docUrl]);
                      }
                    }
                    return true;
                  }}
                >
                  <div >
                    <UploadOutlined />
                    <div style={{ marginTop: 8 }}>Upload</div>
                  </div>
                </Upload>
              </Form.Item>
            </Col>
          </Row>
        </FormContent>
        <FormFooter className='formFooter'>
          <Button
            type='default'
            disabled={isLoading}
            className='btnDimensions'
            onClick={() => props.onCloseDrawer()}
          >
            Cancel
          </Button>
          <Button
            type='primary'
            className='btnDimensions'
            onClick={form.submit}
            disabled={isLoading}
          >
            Save
          </Button>
        </FormFooter>
      </Form>
    </FormLayout>
  );
}
